// src/api/uniboxApi.ts

import { baseApi } from './baseApi';
import type {
  CommunicationThread,
  CommunicationMessage,
  ListThreadsParams,
  ThreadsResponse,
  CreateThreadRequest,
  UpdateThreadRequest,
  ListMessagesParams,
  MessagesResponse,
} from '../types/api';
import { normalizeThreadsListResponse, normalizeMessagesListResponse } from '../utils/listNormalizers';

export const uniboxApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // =============================================
    // Threads
    // =============================================
    getThreads: builder.query<ThreadsResponse, ListThreadsParams>({
      query: (params) => ({
        url: '/unibox/threads',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
          ...(params.q && { q: params.q }),
          ...(params.medium && { medium: params.medium }),
          ...(params.primaryMedium && { primaryMedium: params.primaryMedium }),
          ...(params.contactType && { contactType: params.contactType }),
          ...(params.contactId && { contactId: params.contactId }),
          ...(params.unreadOnly !== undefined && { unreadOnly: String(params.unreadOnly) }),
          ...(params.isArchived !== undefined && { isArchived: String(params.isArchived) }),
          ...(params.limit && { limit: params.limit }),
          ...(params.offset && { offset: params.offset }),
        },
      }),
      transformResponse: (response: unknown) => normalizeThreadsListResponse(response),
      providesTags: (result, error, params) =>
        result
          ? [
              ...result.threads.map(({ id }) => ({ type: 'Thread' as const, id })),
              { type: 'Thread', id: params.workspaceId || 'LIST' },
            ]
          : [{ type: 'Thread', id: params.workspaceId || 'LIST' }],
    }),

    getThread: builder.query<CommunicationThread, string>({
      query: (id) => `/unibox/threads/${id}`,
      providesTags: (result, error, id) => [{ type: 'Thread', id }],
    }),

    createThread: builder.mutation<CommunicationThread, CreateThreadRequest>({
      query: (body) => ({
        url: '/unibox/threads',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'Thread', id: workspaceId || 'LIST' },
      ],
    }),

    updateThread: builder.mutation<CommunicationThread, { id: string } & UpdateThreadRequest>({
      query: ({ id, ...body }) => ({
        url: `/unibox/threads/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Thread', id },
        { type: 'Thread', id: 'LIST' },
      ],
    }),

    // =============================================
    // Messages
    // =============================================
    getMessages: builder.query<MessagesResponse, ListMessagesParams>({
      query: (params) => ({
        url: '/unibox/messages',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
          ...(params.threadId && { threadId: params.threadId }),
          ...(params.medium && { medium: params.medium }),
          ...(params.direction && { direction: params.direction }),
          ...(params.isFavorite !== undefined && { isFavorite: String(params.isFavorite) }),
          ...(params.limit && { limit: params.limit }),
          ...(params.offset && { offset: params.offset }),
        },
      }),
      transformResponse: (response: unknown) => normalizeMessagesListResponse(response),
      providesTags: (result, error, params) =>
        result
          ? [
              ...result.messages.map(({ id }) => ({ type: 'Message' as const, id })),
              { type: 'Message', id: params.threadId || params.workspaceId || 'LIST' },
            ]
          : [{ type: 'Message', id: params.threadId || params.workspaceId || 'LIST' }],
    }),

    getMessage: builder.query<CommunicationMessage, string>({
      query: (id) => `/unibox/messages/${id}`,
      providesTags: (result, error, id) => [{ type: 'Message', id }],
    }),

    toggleMessageFavorite: builder.mutation<CommunicationMessage, { id: string; isFavorite?: boolean }>({
      query: ({ id, isFavorite }) => ({
        url: `/unibox/messages/${id}/favorite`,
        method: 'PATCH',
        body: { isFavorite },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Message', id },
      ],
    }),

    markMessageAsRead: builder.mutation<CommunicationMessage, string>({
      query: (id) => ({
        url: `/unibox/messages/${id}/read`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Message', id },
      ],
    }),

    // =============================================
    // Thread Read Status
    // =============================================
    markThreadAsRead: builder.mutation<{ success: boolean; threadId: string; unreadCount: number }, string>({
      query: (threadId) => ({
        url: `/unibox/threads/${threadId}/read`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, threadId) => [
        { type: 'Thread', id: threadId },
        { type: 'Thread', id: 'LIST' },
      ],
    }),

    // =============================================
    // Threads by Contact
    // =============================================
    getThreadsByContact: builder.query<ThreadsResponse, { contactId: string; limit?: number; offset?: number }>({
      query: ({ contactId, limit, offset }) => ({
        url: `/unibox/threads/contact/${contactId}`,
        params: {
          ...(limit && { limit }),
          ...(offset && { offset }),
        },
      }),
      transformResponse: (response: unknown) => normalizeThreadsListResponse(response),
      providesTags: (result, error, { contactId }) =>
        result
          ? [
              ...result.threads.map(({ id }) => ({ type: 'Thread' as const, id })),
              { type: 'Thread', id: `contact-${contactId}` },
            ]
          : [{ type: 'Thread', id: `contact-${contactId}` }],
    }),

    // =============================================
    // Send Message
    // =============================================
    sendMessage: builder.mutation<
      { success: boolean; messageId?: string; externalMessageId?: string; error?: string; retryable?: boolean },
      {
        threadId: string;
        medium: 'sms' | 'email' | 'in_app';
        toAddress: string;
        subject?: string;
        bodyText?: string;
        bodyHtml?: string;
        ccAddresses?: string[];
        bccAddresses?: string[];
        attachments?: { id: string; filename: string; mimeType: string; size: number; url?: string }[];
        emailProvider?: 'gmail' | 'outlook';
        emailIntegrationId?: string;
        inReplyToMessageId?: string;
      }
    >({
      query: (body) => ({
        url: '/unibox/messages/send',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { threadId }) => [
        { type: 'Message', id: threadId },
        { type: 'Thread', id: threadId },
        { type: 'Thread', id: 'LIST' },
      ],
    }),

    // =============================================
    // Retry Failed Message
    // =============================================
    retryMessage: builder.mutation<
      { success: boolean; messageId?: string; error?: string },
      string
    >({
      query: (messageId) => ({
        url: `/unibox/messages/${messageId}/retry`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, messageId) => [
        { type: 'Message', id: messageId },
      ],
    }),

    // =============================================
    // Chat Threads (Internal Team Chat)
    // =============================================
    createChatThread: builder.mutation<
      CommunicationThread,
      {
        workspaceId?: string;
        name?: string;
        participantUserIds: string[];
        initialMessage?: {
          bodyText: string;
        };
      }
    >({
      query: (body) => ({
        url: '/unibox/threads/chat',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'Thread', id: result.id },
              { type: 'Thread', id: 'LIST' },
            ]
          : [{ type: 'Thread', id: 'LIST' }],
    }),

    sendChatMessage: builder.mutation<
      CommunicationMessage,
      {
        threadId: string;
        bodyText: string;
        inReplyToMessageId?: string;
      }
    >({
      query: ({ threadId, bodyText, inReplyToMessageId }) => ({
        url: `/unibox/threads/${threadId}/messages/chat`,
        method: 'POST',
        body: { bodyText, inReplyToMessageId },
      }),
      invalidatesTags: (result, error, { threadId }) => [
        { type: 'Message', id: threadId },
        { type: 'Thread', id: threadId },
        { type: 'Thread', id: 'LIST' },
      ],
    }),

    // =============================================
    // Get Available Email Providers
    // =============================================
    getEmailProviders: builder.query<
      { provider: 'gmail' | 'outlook'; integrationId: string; email?: string }[],
      void
    >({
      query: () => '/unibox/providers/email',
    }),

    // =============================================
    // Unified Thread — get or create per-contact thread
    // =============================================
    getOrCreateUnifiedThread: builder.mutation<
      CommunicationThread & {
        contact: { id: string; fullName: string | null; email: string | null; phone: string | null } | null;
      },
      { contactId: string }
    >({
      query: (body) => ({
        url: '/unibox/threads/unified',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Thread', id: 'LIST' }],
    }),

    // =============================================
    // Check SMS Availability
    // =============================================
    checkSmsAvailable: builder.query<
      {
        available: boolean;
        mode?: 'native' | 'outbound_managed' | 'unavailable';
        managedByOutbound?: boolean;
        fromNumber?: string | null;
        reason?: string | null;
      },
      void
    >({
      query: () => '/unibox/providers/sms/available',
    }),

    // =============================================
    // Message Reactions
    // =============================================
    getMessageReactions: builder.query<
      { reactions: { emoji: string; count: number; userIds: string[] }[] },
      string
    >({
      query: (messageId) => `/unibox/messages/${messageId}/reactions`,
      providesTags: (result, error, messageId) => [
        { type: 'MessageReaction' as const, id: messageId },
      ],
    }),

    toggleMessageReaction: builder.mutation<
      {
        action: 'added' | 'removed';
        messageId: string;
        emoji: string;
        reactions: { emoji: string; count: number; userIds: string[] }[];
      },
      { messageId: string; emoji: string }
    >({
      query: ({ messageId, emoji }) => ({
        url: `/unibox/messages/${messageId}/reactions/toggle`,
        method: 'POST',
        body: { emoji },
      }),
      invalidatesTags: (result, error, { messageId }) => [
        { type: 'MessageReaction' as const, id: messageId },
        { type: 'Message', id: messageId },
      ],
    }),
  }),
});

export const {
  useGetThreadsQuery,
  useGetThreadQuery,
  useCreateThreadMutation,
  useUpdateThreadMutation,
  useGetMessagesQuery,
  useGetMessageQuery,
  useToggleMessageFavoriteMutation,
  useMarkMessageAsReadMutation,
  useMarkThreadAsReadMutation,
  useGetThreadsByContactQuery,
  useSendMessageMutation,
  useRetryMessageMutation,
  useCreateChatThreadMutation,
  useSendChatMessageMutation,
  useGetOrCreateUnifiedThreadMutation,
  useGetEmailProvidersQuery,
  useCheckSmsAvailableQuery,
  useGetMessageReactionsQuery,
  useToggleMessageReactionMutation,
} = uniboxApi;
